import { useState } from 'react';
import type React from 'react';

import { CheckIcon, ChevronDown, XCircle, XIcon } from 'lucide-react';
import { AnimatePresence, motion, type Transition } from 'motion/react';

import { Badge } from '@/shadcn/badge';
import { Button } from '@/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/shadcn/command';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/shadcn/hover-card';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';
import { Separator } from '@/shadcn/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { isLockedWidget } from '@/components/forms/utils/links';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { usePropsSynchronization } from '@/hooks/usePropsSynchronization';
import { arraysMatch } from '../checkboxes/checkboxes.utils';
import type { WidgetWriterProps } from '../widget.type';

type Label = string;

export type MultiSelectOption = {
  label: string;
  value: string;
  iconName?: string;
};

type UIOptions = {
  enumOptions: MultiSelectOption[];
  itemName?: string;
  placeholder?: string;
  maxVisibleItems?: number;
};

type Props = {
  options: UIOptions;
  value: Label[];
  enum?: MultiSelectOption[];
  itemName?: string;
  maxVisibleItems?: number;
  placeholder?: string;
} & WidgetWriterProps<unknown>;

const transition: Transition = {
  type: 'spring',
  duration: 0.3,
  bounce: 0.1
};

export const MultiSelectMenu = (rjsfProps: Props) => {
  const props = useDynamicProps(rjsfProps);
  usePropsSynchronization(props, rjsfProps);

  const options =
    props.enum || props.options?.enumOptions || ([] as MultiSelectOption[]);

  const placeholder =
    props.placeholder ??
    props.options?.placeholder ??
    `Select ${props.itemName || props.options?.itemName || 'item'}(s)`;

  /**
   * Building an array of string values
   * in order to match with an array of results values.
   */
  const enumOptionsValues: string[] = options.map(e => e.value);

  const [tempEnumOptions, setTempEnumOptions] = useState(enumOptionsValues);

  const maxVisibleItems =
    props.maxVisibleItems ?? props.options?.maxVisibleItems ?? 10;
  /**
   * Check if the selectMenu option value has changed.
   * If so, we clear the array of results values.
   */
  if (!arraysMatch(enumOptionsValues, tempEnumOptions)) {
    setTempEnumOptions(enumOptionsValues);
    props.onChange([]);
  }

  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(props.id, props.formContext.links);
  const isDisabled = props.disabled || isLocked;

  const onValueChange = (value: string[]) => {
    if (!isDisabled) {
      props.onChange(value);
    }
  };

  const [selectedValues, setSelectedValues] = useState<string[]>(
    props.value || []
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsPopoverOpen(true);
    } else if (event.key === 'Backspace' && !event.currentTarget.value) {
      const newSelectedValues = [...selectedValues];
      newSelectedValues.pop();
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    }
  };

  const toggleOption = (option: string) => {
    const newSelectedValues = selectedValues.includes(option)
      ? selectedValues.filter(value => value !== option)
      : [...selectedValues, option];
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onValueChange([]);
  };

  const handleTogglePopover = () => {
    setIsPopoverOpen(prev => !prev);
  };

  const clearExtraOptions = () => {
    const newSelectedValues = selectedValues.slice(0, maxVisibleItems);
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
  };

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      handleClear();
    } else {
      const allValues = options.map(option => option.value);
      setSelectedValues(allValues);
      onValueChange(allValues);
    }
  };

  return (
    <Popover modal={false} onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={isPopoverOpen}
          aria-haspopup="listbox"
          aria-invalid={props.invalid}
          aria-label={`Click to open the ${props.name} multi-select menu`}
          autoFocus={props.autofocus}
          className={cn(
            'flex h-auto min-h-10 w-full items-center justify-between overflow-hidden rounded-md border border-input bg-inherit py-[7px] pr-0 pl-3 font-normal normal-case tracking-normal hover:bg-inherit active:scale-100 disabled:pointer-events-auto disabled:cursor-auto disabled:opacity-70 [&_svg]:pointer-events-auto',
            selectedValues.length === 0 && 'hover:bg-accent'
          )}
          data-cy={`multiselect-${props.name}`}
          disabled={isDisabled}
          id={props.id}
          onClick={handleTogglePopover}
        >
          {selectedValues.length > 0 ? (
            <div className="flex h-full w-full items-center justify-between">
              <div className="relative flex flex-wrap items-center gap-1.5 text-sm">
                <AnimatePresence mode="popLayout">
                  <TooltipProvider>
                    {selectedValues.slice(0, maxVisibleItems).map(value => {
                      const option = options.find(o => o.value === value);
                      const id = `${props.name}-${value}`;
                      return (
                        <motion.div
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{
                            opacity: 0,
                            scale: 0.5
                          }}
                          initial={{
                            opacity: 0,
                            scale: 0.8
                          }}
                          key={id}
                          layout
                          transition={transition}
                        >
                          <Badge
                            className="rounded-full bg-accent text-accent-foreground text-xs hover:shadow dark:hover:shadow-lg"
                            data-cy={`multiselect-${props.name}-badge-${value}`}
                            variant="card"
                          >
                            {option?.iconName && (
                              <div className="mr-2">
                                <DynamicIcon icon={option.iconName} size={16} />
                              </div>
                            )}
                            {option?.label}
                            {!isDisabled && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className="group ml-2 flex size-4 cursor-pointer items-center justify-center rounded-full"
                                    data-cy={`multiselect-${props.name}-item-${value}-clear`}
                                    onClick={e => {
                                      e.stopPropagation();
                                      toggleOption(value);
                                    }}
                                  >
                                    <XCircle className="h-3.5! text-muted-foreground transition-colors group-hover:text-foreground" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Remove the{' '}
                                  <b className="font-semibold">
                                    {option?.label}
                                  </b>{' '}
                                  item
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </Badge>
                        </motion.div>
                      );
                    })}
                    {selectedValues.length > maxVisibleItems && (
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        transition={transition}
                      >
                        <HoverCard openDelay={0}>
                          <HoverCardTrigger>
                            <Badge
                              className="rounded-full bg-accent text-accent-foreground text-xs hover:shadow dark:hover:shadow-lg"
                              data-cy={`multiselect-${props.name}-hidden-items`}
                              variant="card"
                            >
                              <p>
                                +{' '}
                                <span
                                  data-cy={`multiselect-${props.name}-hidden-items-count`}
                                >
                                  {selectedValues.length - maxVisibleItems}
                                </span>{' '}
                                more
                              </p>
                              {!isDisabled && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      aria-label="Remove all additional selected items"
                                      className="group ml-2 flex size-4 cursor-pointer items-center justify-center rounded-full"
                                      data-cy={`multiselect-${props.name}-hidden-items-clear`}
                                      onClick={event => {
                                        event.stopPropagation();
                                        clearExtraOptions();
                                      }}
                                    >
                                      <XCircle className="h-3.5! text-muted-foreground transition-colors group-hover:text-foreground" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Remove all additional selected items
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </Badge>
                          </HoverCardTrigger>
                          <HoverCardContent
                            align="start"
                            className="p-1.5"
                            onClick={e => e.stopPropagation()}
                            sideOffset={10}
                          >
                            <div className="max-h-60 w-full overflow-auto">
                              {selectedValues
                                .slice(maxVisibleItems)
                                .map(value => {
                                  const option = options.find(
                                    o => o.value === value
                                  );
                                  if (!option) return null;

                                  return (
                                    <div
                                      className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent"
                                      key={value}
                                    >
                                      {option.iconName && (
                                        <div
                                          className="mr-2 text-muted-foreground"
                                          data-cy={`multiselect-${props.name}-item-${option.value}-icon`}
                                        >
                                          <DynamicIcon
                                            icon={option.iconName}
                                            size={16}
                                          />
                                        </div>
                                      )}
                                      <span className="truncate">
                                        {option?.label}
                                      </span>
                                      {!isDisabled && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              aria-label={`Remove the ${option?.label} item`}
                                              className="group ml-2 flex size-4 cursor-pointer items-center justify-center rounded-full"
                                              data-cy={`multiselect-${props.name}-hidden-items-clear`}
                                              onClick={event => {
                                                event.stopPropagation();
                                                toggleOption(value);
                                              }}
                                            >
                                              <XCircle className="h-3.5! text-muted-foreground transition-colors group-hover:text-foreground" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Remove the{' '}
                                            <b className="font-semibold">
                                              {option?.label}
                                            </b>{' '}
                                            item
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </motion.div>
                    )}
                  </TooltipProvider>
                </AnimatePresence>
              </div>

              {!isDisabled && (
                <div className="flex h-full items-center justify-between">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <XIcon
                          aria-label="Deselect all options"
                          className="mx-2 h-4 cursor-pointer text-muted-foreground hover:text-destructive"
                          data-cy={`multiselect-${props.name}-deselect-all`}
                          onClick={event => {
                            event.stopPropagation();
                            handleClear();
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>Deselect all</TooltipContent>
                    </Tooltip>
                    <Separator
                      className="flex h-full min-h-6"
                      orientation="vertical"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <ChevronDown className="mx-2 h-4 origin-top cursor-pointer text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>Open options</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          ) : (
            <div className="mx-auto flex w-full items-center justify-between">
              <span
                className="text-muted-foreground text-sm"
                data-cy={`multiselect-${props.name}-placeholder`}
              >
                {placeholder}
              </span>
              <ChevronDown className="mx-2 h-4 cursor-pointer text-muted-foreground" />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
      >
        <Command>
          <CommandInput
            data-cy={`multiselect-${props.name}-input`}
            onKeyDown={handleInputKeyDown}
            placeholder="Search..."
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                className="cursor-pointer"
                key="all"
                onSelect={toggleAll}
              >
                <div
                  className={cn(
                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-input',
                    selectedValues.length === options.length
                      ? 'bg-primary'
                      : 'opacity-50 [&_svg]:invisible'
                  )}
                  data-cy="multiselect-select-all"
                >
                  <CheckIcon className="h-4 w-4 text-white" />
                </div>
                <span>(Select All)</span>
              </CommandItem>
              {options.map(option => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <CommandItem
                    className="cursor-pointer"
                    data-cy={`multiselect-${props.name}-item-${option.value}`}
                    key={option.value}
                    onSelect={() => toggleOption(option.value)}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-input [&svg]:text-white',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <CheckIcon className="h-4 w-4 text-white" />
                    </div>
                    {option.iconName && (
                      <div
                        className="mr-2 text-muted-foreground"
                        data-cy={`multiselect-${props.name}-item-${option.value}-icon`}
                      >
                        <DynamicIcon icon={option.iconName} size={16} />
                      </div>
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <div className="flex items-center justify-between">
                {selectedValues.length > 0 && (
                  <>
                    <CommandItem
                      className="flex-1 cursor-pointer justify-center"
                      data-cy={`multiselect-${props.name}-clear-btn`}
                      onSelect={handleClear}
                    >
                      Clear
                    </CommandItem>
                    <Separator
                      className="flex h-full min-h-6"
                      orientation="vertical"
                    />
                  </>
                )}
                <CommandItem
                  className="max-w-full flex-1 cursor-pointer justify-center"
                  data-cy={`multiselect-${props.name}-close-btn`}
                  onSelect={() => setIsPopoverOpen(false)}
                >
                  Close
                </CommandItem>
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
